import React from 'react'

import { ArrowCircleSvg } from '@svg/react'

import {
  CarouselControlDirection,
  CarouselControlType,
  SlideInterface,
} from '@adUtilities/constants/carousel'

import LabelControl from './carousel-controls/label-control'
import Scrollbar from './carousel-controls/scrollbar'
import ThumbnailControl from './carousel-controls/thumbnail-control'
import { setScrollPosition } from './helper'

interface ComponentProps {
  type: CarouselControlType
  items: Array<SlideInterface>
  currentSlideIndex: number
  alignClass?: string
  toggleControls?: boolean
  className?: string
  disableInfiniteScroll?: boolean
  handleItemClick: (itemIndex: number) => void
  direction?: CarouselControlDirection
}

function CarouselControl({
  type,
  items,
  currentSlideIndex,
  alignClass,
  toggleControls,
  className = '',
  disableInfiniteScroll = false,
  handleItemClick,
  direction = CarouselControlDirection.Horizontal,
}: ComponentProps) {
  const dotContainerRef = React.useRef<HTMLDivElement>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const nextButtonClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      if (
        (disableInfiniteScroll && currentSlideIndex === items.length - 1) ||
        !toggleControls
      ) {
        return
      }
      handleItemClick(
        currentSlideIndex + 1 === items.length ? 0 : currentSlideIndex + 1
      )
    },
    [currentSlideIndex, toggleControls, disableInfiniteScroll]
  )

  const previousButtonClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      if (
        (disableInfiniteScroll && currentSlideIndex === 0) ||
        !toggleControls
      ) {
        return
      }
      handleItemClick(
        currentSlideIndex === 0 ? items.length - 1 : currentSlideIndex - 1
      )
    },
    [currentSlideIndex, toggleControls, disableInfiniteScroll]
  )

  const dotAlignStyle = () =>
    type === CarouselControlType.Thumbnail ? 'text-center' : 'text-left'

  React.useEffect(
    () => setScrollPosition(dotContainerRef, direction),
    [currentSlideIndex, direction]
  )

  React.useEffect(() => {
    const preventWheel = (e: WheelEvent) => e.preventDefault()
    if (direction === CarouselControlDirection.Vertical) {
      containerRef.current?.addEventListener('wheel', preventWheel, {
        passive: false,
      })
    }
    return () =>
      containerRef.current?.removeEventListener('wheel', preventWheel)
  }, [direction])

  return (
    <div
      ref={containerRef}
      className={`flex ${alignClass || dotAlignStyle()} ${
        direction === CarouselControlDirection.Horizontal
          ? 'gap-4'
          : 'flex-col items-center ipad-mini:gap-3 ipad-mini-land:gap-1 galaxy-tab-land:gap-2 ipad-pro-land:gap-3'
      } ${className}`}
    >
      {items.length > 3 && (
        <div
          className={`${
            type === CarouselControlType.Thumbnail &&
            `flex ${
              direction === CarouselControlDirection.Horizontal
                ? 'h-[85px]'
                : 'ml-[21px] h-[60px]'
            } items-center justify-center`
          }`}
        >
          <button
            type="button"
            className={`rounded-full drop-shadow-md ${
              type === CarouselControlType.Label && '-mt-1'
            } ${
              disableInfiniteScroll && currentSlideIndex === 0
                ? 'cursor-not-allowed text-gray-300'
                : 'text-white'
            }`}
            onClick={previousButtonClick}
          >
            <ArrowCircleSvg
              size="m"
              rotate={
                direction === CarouselControlDirection.Horizontal
                  ? 'left'
                  : 'top'
              }
            />
          </button>
        </div>
      )}

      <div
        className={`flex-1 ${
          direction === CarouselControlDirection.Horizontal
            ? 'max-w-[250px] ipad-mini-land:max-w-[350px] ipad-pro-land:max-w-[30vw]'
            : 'flex flex-row-reverse'
        } ${
          direction === CarouselControlDirection.Vertical && items.length > 3
            ? 'max-h-[293px] ipad-mini-land:max-h-[220px] galaxy-tab-land:max-h-[250px] ipad-pro-land:max-h-[293px]'
            : ''
        }`}
      >
        <div
          ref={dotContainerRef}
          className={`no-scrollbar relative flex items-center pb-1 ${
            direction === CarouselControlDirection.Horizontal
              ? 'gap-4 overflow-x-auto'
              : 'flex-col overflow-y-auto ipad-mini:gap-3 ipad-mini-land:gap-1 galaxy-tab-land:gap-2 ipad-pro-land:gap-3'
          }`}
        >
          {items.length > 0 &&
            type === CarouselControlType.Thumbnail &&
            items.map((item, index) => (
              <div key={`${index as number}`} className="shrink-0">
                <ThumbnailControl
                  item={item}
                  active={index === currentSlideIndex}
                  handleItemClick={() => handleItemClick(index)}
                  disabled={!toggleControls}
                />
              </div>
            ))}

          {items.length > 0 &&
            type === CarouselControlType.Label &&
            items.map((item, index) => (
              <div key={`${index as number}`} className="shrink-0">
                <LabelControl
                  item={item}
                  active={index === currentSlideIndex}
                  handleItemClick={() => handleItemClick(index)}
                  disabled={!toggleControls}
                />
              </div>
            ))}
        </div>

        {items.length > 3 && (
          <div
            className={`rounded-track drop-shadow-md ${
              direction === CarouselControlDirection.Horizontal
                ? 'mt-2 w-full'
                : 'mr-4'
            }`}
          >
            <Scrollbar
              currentSlideIndex={currentSlideIndex}
              totalItem={items.length}
              handleItemClick={handleItemClick}
              direction={direction}
            />
          </div>
        )}
      </div>

      {items.length > 3 && (
        <div
          className={`${
            type === CarouselControlType.Thumbnail &&
            `flex ${
              direction === CarouselControlDirection.Horizontal
                ? 'h-[85px]'
                : 'ml-[21px] h-[60px]'
            } items-center justify-center`
          }`}
        >
          <button
            type="button"
            className={`rounded-full drop-shadow-md ${
              type === CarouselControlType.Label && '-mt-1'
            } ${
              disableInfiniteScroll && currentSlideIndex !== items.length - 1
                ? 'cursor-not-allowed text-gray-300'
                : 'text-white'
            }`}
            onClick={nextButtonClick}
          >
            <ArrowCircleSvg
              size="m"
              rotate={
                direction === CarouselControlDirection.Horizontal
                  ? 'right'
                  : 'down'
              }
            />
          </button>
        </div>
      )}
    </div>
  )
}

export default CarouselControl
